import React from 'react';
import { useAdminQueue } from '../../../contexts/AdminQueueContext';
import { RefreshCw, Activity, Clock, CheckCircle, XCircle, AlertTriangle, Database, Server } from 'lucide-react';

const AdminDashboard = () => {
	const { queueData, isLoading, error, refreshQueueData, lastUpdated } = useAdminQueue();

	const getStatusIcon = (status) => {
		switch (status) {
			case 'healthy':
			case 'active':
				return <CheckCircle className="w-5 h-5 text-green-600" />;
			case 'warning':
				return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
			case 'error':
			case 'failed':
				return <XCircle className="w-5 h-5 text-red-600" />;
			default:
				return <Activity className="w-5 h-5 text-muted-foreground" />;
		}
	};

	if (isLoading && !queueData) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="flex items-center space-x-3">
					<RefreshCw className="w-6 h-6 animate-spin text-brand-600" />
					<span className="text-lg text-muted-foreground">Loading admin dashboard...</span>
				</div>
			</div>
		);
	}

	return (
		<div>
			<div>
				{/* Section header - AdminPage owns the page h1 */}
				<div className="flex items-center justify-between mb-8">
					<div>
						<h2 className="text-lg font-semibold text-foreground">System overview</h2>
						<p className="text-sm text-muted-foreground mt-1">Queue activity and system health</p>
					</div>
					<div className="flex items-center space-x-4">
						{lastUpdated && (
							<span className="text-sm text-muted-foreground">
								Last updated: {lastUpdated.toLocaleTimeString()}
							</span>
						)}
						<button
							onClick={refreshQueueData}
							disabled={isLoading}
							className="flex items-center space-x-2 bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700 disabled:opacity-50"
						>
							<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
							<span>Refresh</span>
						</button>
					</div>
				</div>

				{error && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 dark:bg-red-500/10 dark:border-red-500/30">
						<div className="flex items-center">
							<XCircle className="w-5 h-5 text-red-600 mr-2" />
							<span className="text-red-800">{error}</span>
						</div>
					</div>
				)}

				{/* System Health Overview */}
				<div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
					{/* Queue Health */}
					<div className="bg-card rounded-lg shadow-sm border border-border p-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-sm font-medium text-muted-foreground">Queue Status</h3>
							{queueData.activeScans > 0 ? (
								<CheckCircle className="w-5 h-5 text-green-600" />
							) : (
								<XCircle className="w-5 h-5 text-red-600" />
							)}
						</div>
						<div className="text-2xl font-bold text-foreground mb-2">
							{queueData.activeScans > 0 ? 'Active' : 'Inactive'}
						</div>
						<div className="text-sm text-muted-foreground">
							Processing: {queueData.activeScans || 0} scans
						</div>
					</div>

					{/* Pending Scans */}
					<div className="bg-card rounded-lg shadow-sm border border-border p-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-sm font-medium text-muted-foreground">Pending Scans</h3>
							<Clock className="w-5 h-5 text-brand-600" />
						</div>
						<div className="text-2xl font-bold text-foreground mb-2">
							{queueData.pendingScans || 0}
						</div>
						<div className="text-sm text-muted-foreground">
							In queue waiting
						</div>
					</div>

					{/* Active Scans */}
					<div className="bg-card rounded-lg shadow-sm border border-border p-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-sm font-medium text-muted-foreground">Active Scans</h3>
							<Activity className="w-5 h-5 text-orange-600" />
						</div>
						<div className="text-2xl font-bold text-foreground mb-2">
							{queueData.activeScans || 0}
						</div>
						<div className="text-sm text-muted-foreground">
							Currently processing
						</div>
					</div>

					{/* System Health */}
					<div className="bg-card rounded-lg shadow-sm border border-border p-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-sm font-medium text-muted-foreground">System Health</h3>
							{getStatusIcon(queueData.systemHealth)}
						</div>
						<div className="text-2xl font-bold text-foreground mb-2">
							{queueData.systemHealth || 'Unknown'}
						</div>
						<div className="text-sm text-muted-foreground">
							Overall status
						</div>
					</div>
				</div>

				{/* Detailed Status Cards */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{/* Queue Details */}
					<div className="bg-card rounded-lg shadow-sm border border-border">
						<div className="p-6 border-b border-border">
							<h3 className="text-lg font-semibold text-foreground flex items-center">
								<Database className="w-5 h-5 mr-2" />
								Queue Statistics
							</h3>
						</div>
						<div className="p-6">
							<div className="space-y-4">
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">Pending Scans</span>
									<span className="font-semibold">{queueData.pendingScans || 0}</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">Active Scans</span>
									<span className="font-semibold">{queueData.activeScans || 0}</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">Completed Today</span>
									<span className="font-semibold text-green-600">{queueData.completedToday || 0}</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">Failed Today</span>
									<span className="font-semibold text-red-600">{queueData.failedToday || 0}</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">System Health</span>
									<div className="flex items-center space-x-2">
										{getStatusIcon(queueData.systemHealth)}
										<span className={`font-semibold ${queueData.systemHealth === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
											{queueData.systemHealth || 'Unknown'}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* System Health */}
					<div className="bg-card rounded-lg shadow-sm border border-border">
						<div className="p-6 border-b border-border">
							<h3 className="text-lg font-semibold text-foreground flex items-center">
								<Server className="w-5 h-5 mr-2" />
								System Health
							</h3>
						</div>
						<div className="p-6">
							<div className="space-y-4">
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">System Status</span>
									<div className="flex items-center space-x-2">
										{getStatusIcon(queueData.systemHealth)}
										<span className={`font-semibold ${queueData.systemHealth === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
											{queueData.systemHealth || 'Unknown'}
										</span>
									</div>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">Last Updated</span>
									<span className="font-semibold text-sm">
										{lastUpdated ? lastUpdated.toLocaleTimeString() : 'N/A'}
									</span>
								</div>
								<div className="flex justify-between items-center">
									<span className="text-muted-foreground">Real-time Updates</span>
									<div className="flex items-center space-x-2">
										{queueData.systemHealth === 'healthy' ? (
											<>
												{getStatusIcon('healthy')}
												<span className="font-semibold text-green-600">Enabled</span>
											</>
										) : (
											<>
												{getStatusIcon('error')}
												<span className="font-semibold text-red-600">Disabled</span>
											</>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AdminDashboard;
